import { Button, Box } from '@mantine/core'
import { ReactQuery } from 'cms-api'
import { partition, sortBy } from 'lodash'
import React from 'react'
import { useInfiniteQuery } from 'react-query'
import { getStrapiRequestParams } from '@admin/utils/strapi-params'
import { Grid } from 'components/Layout'
import {
  ProductThumbnailCard,
  ProductThumbnailList,
} from 'components/ProductThumbnail'
import { WrappedLoader } from 'components/WrappedLoader'
import { PRODUCT_VARIANT_STATUSES } from 'constants/common'
import { SPACE } from 'Theme'
import { getRangeDiscountBadge } from 'utils/formatters'
import { getProductRoute } from 'utils/getProductRoute'
import { getProductSlug, getVariantSlug } from 'utils/getProductSlug'

const PAGE_SIZE = 8

export const sortProductVariantsByPriceAndQuantity = (
  variants: ReactQuery.ProductVariantWithLegacyId[]
) => {
  const [availableVariants, notAvailableVariants] = partition(
    variants,
    ({ productVariantInventory, leadTimeInWeeks }) => {
      const leadTime = leadTimeInWeeks ?? 0
      const quantity = productVariantInventory?.quantity ?? 0
      return quantity > 0 || leadTime > 0
    }
  ).map((partitionedVariants) =>
    sortBy(partitionedVariants, 'manufacturersSuggestedRetailPrice')
  )
  return [...availableVariants, ...notAvailableVariants]
}

export const getSelectedVariant = (
  variants: ReactQuery.ProductVariantWithLegacyId[]
) => {
  const variantWithLeadImage = variants?.find(
    (v) => v.leadProductImage !== null
  )
  if (
    variantWithLeadImage &&
    variantWithLeadImage?.productVariantStatus ===
      PRODUCT_VARIANT_STATUSES.PUBLISHED
  ) {
    return variantWithLeadImage
  }

  const sortedVariants = sortProductVariantsByPriceAndQuantity(variants ?? [])

  const sortedByStatus = sortedVariants.sort((a, b) => {
    // Prioritize PUBLISHED with quantity > 0
    if (
      a.productVariantStatus === 'PUBLISHED' &&
      a.productVariantInventory.quantity > 0 &&
      (b.productVariantStatus !== 'PUBLISHED' ||
        b.productVariantInventory.quantity === 0)
    ) {
      return -1
    }
    if (
      b.productVariantStatus === 'PUBLISHED' &&
      b.productVariantInventory.quantity > 0 &&
      (a.productVariantStatus !== 'PUBLISHED' ||
        a.productVariantInventory.quantity === 0)
    ) {
      return 1
    }

    // Prioritize PUBLISHED (regardless of quantity)
    if (
      a.productVariantStatus === 'PUBLISHED' &&
      b.productVariantStatus !== 'PUBLISHED'
    ) {
      return -1
    }
    if (
      b.productVariantStatus === 'PUBLISHED' &&
      a.productVariantStatus !== 'PUBLISHED'
    ) {
      return 1
    }

    return 0 // Keep original order for other cases
  })

  return sortedByStatus[0]
}

export const UserRecentlyViewedProducts = ({
  isListView,
  searchString,
}: {
  isListView?: boolean
  searchString: string
}) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch: refetchRecentlyViewerProducts,
  } = useInfiniteQuery(
    ['userRecentlyViewedProducts', searchString],
    async ({ pageParam = 1 }) => {
      const response = await ReactQuery.getRecentlyViewedUserProductHistory({
        populate: {
          product: {
            populate: {
              images: true,
              brand: true,
              productVariants: {
                populate: {
                  productVariantInventory: true,
                  images: true,
                  leadProductImage: true,
                },
              },
            },
          },
        } as never,
        filters: {
          $or: [
            {
              product: {
                title: {
                  $containsi: searchString,
                },
              },
            },

            {
              product: {
                brand: {
                  name: {
                    $containsi: searchString,
                  },
                },
              },
            },
          ],
        },
        ...getStrapiRequestParams(
          { pageIndex: pageParam - 1, pageSize: PAGE_SIZE },
          [
            {
              id: 'updatedAt',
              desc: true,
            },
          ],
          []
        ),
      })
      return response
    },
    {
      getNextPageParam: (lastPage) => {
        const currentPage = lastPage?.meta?.pagination?.page
        const totalPages = lastPage?.meta?.pagination?.pageCount
        return currentPage < totalPages ? currentPage + 1 : undefined
      },
    }
  )

  const products = data?.pages.flatMap((page) => page?.data || [])

  return (
    <Box
      py={SPACE.PX_32}
      px={{
        base: isListView ? 0 : SPACE.PX_16,
        md: isListView ? 0 : SPACE.PX_32,
      }}
    >
      {isLoading ? (
        <WrappedLoader />
      ) : (
        <>
          {' '}
          <Grid
            gridTemplateColumns={{
              MOBILE: isListView ? '1fr' : '1fr 1fr',
              TABLET: isListView ? '1fr' : 'repeat(4, 1fr)',
            }}
            gap={{
              MOBILE: isListView ? 0 : SPACE.PX_16,
              TABLET: isListView ? 0 : SPACE.PX_32,
            }}
          >
            {products.map(({ product }) => {
              const mainVariant = getSelectedVariant(product.productVariants)
              const tradeDiscount = getRangeDiscountBadge({
                priceMin: mainVariant.manufacturersSuggestedRetailPrice,
                priceMax: mainVariant.manufacturersSuggestedRetailPrice,
                tradePriceMin: mainVariant.tradePrice,
                tradePriceMax: mainVariant.tradePrice,
              })

              return isListView ? (
                <ProductThumbnailList
                  variantId={mainVariant.legacyId}
                  key={product.id}
                  title={product.title}
                  productImageAlt={
                    mainVariant.leadProductImage?.alternativeText ||
                    product.title
                  }
                  productImageUrl={
                    mainVariant.leadProductImage?.url ||
                    mainVariant.images[0].url
                  }
                  price={mainVariant.manufacturersSuggestedRetailPrice}
                  tradePrice={mainVariant.tradePrice}
                  url={getProductRoute({
                    slug: getProductSlug({
                      id: product.legacyId,
                      title: product.title,
                    }),
                    variantSlug: getVariantSlug({
                      id: mainVariant.legacyId,
                      title: product.title,
                    }),
                  })}
                  onRefetch={refetchRecentlyViewerProducts}
                />
              ) : (
                <ProductThumbnailCard
                  variantId={mainVariant.legacyId}
                  productId={product.legacyId}
                  //  Todo - revisit
                  breakoutOptionCount={0}
                  key={product.id}
                  title={product.title}
                  productImageAlt={
                    mainVariant.leadProductImage?.alternativeText ||
                    product.title
                  }
                  productImageUrl={
                    mainVariant.leadProductImage?.url ||
                    mainVariant.images[0].url
                  }
                  price={mainVariant.manufacturersSuggestedRetailPrice}
                  tradePrice={mainVariant.tradePrice}
                  url={getProductRoute({
                    slug: getProductSlug({
                      id: product.legacyId,
                      title: product.title,
                    }),
                    variantSlug: getVariantSlug({
                      id: mainVariant.legacyId,
                      title: product.title,
                    }),
                  })}
                  onRefetch={refetchRecentlyViewerProducts}
                  tradeDiscount={tradeDiscount}
                  brand={(product.brand as any)?.name}
                />
              )
            })}
          </Grid>
          {hasNextPage && (
            <Box mt={SPACE.PX_32} ta="center">
              <Button
                onClick={() => fetchNextPage()}
                loading={isFetchingNextPage}
                variant="outline"
              >
                Load More
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}
